import React, { useContext, useEffect, useState } from 'react'
import { Button, ListMenu, ListMenuButton, Modal, NotificationInline, NotificationManagerContext,
   Spacer, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { Link, useRouteMatch } from 'react-router-dom'
import { LogoOutline24Icon, MoreIcon } from 'assets/icons'
import { adminAppUrl, adminCustomPageUrl } from 'config/constants/urls'
import ActionListItemRow from 'src/components/Settings/ActionListItemRow'
import ProtoHeadingBlock from 'src/components/Settings/ProtoHeadingBlock'
import ProtoReturnBar from 'src/components/Settings/ProtoReturnBar'
import ModalRoute from 'src/components/utils/ModalRoute'
import Popover from 'src/components/utils/Popover'
import { useOrganizationWithOptions, useUpdateOrganization } from 'src/graphql/hooks'
import ThemeContext from 'src/libraries/contexts/Theme'
import LogoEditor from '~/apps/LogoEditor'
import NavLinkListItemRow from '~/components/NavLinkListItemRow'
import LinkCreate from './LinkCreate'
import LinkEdit from './LinkEdit'

const AdminWorkspaceMainMenu = ({ returnLinkName, returnLinkTo }) => {
   const match = useRouteMatch()
   const theme = useContext(ThemeContext)
   const { showNotification } = useContext(NotificationManagerContext)
   const [linkItems, setLinkItems] = useState([])

   const { organization, loading } = useOrganizationWithOptions({
      withGlobalNavLinksTargetMetadata: true
   })

   const { updateOrganization, mutating } = useUpdateOrganization({
      variables: {
         withGlobalNavLinksTargetMetadata: true
      }
   })

   const handleDragEnd = async (result) => {
      const newLinkItems = [...linkItems]
      const [removed] = newLinkItems.splice(result.source.index, 1)
      newLinkItems.splice(result.destination.index, 0, removed)
      const newLinkIds = newLinkItems.map(l => l.id)
      setLinkItems(newLinkItems)

      try {
         await updateOrganization({
            globalNavLinks: [{
               operation: 'reorder',
               reorder: newLinkIds
            }]
         })

         showNotification({
            message: 'Updated order for Main Menu links successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         showNotification({
            message: err?.graphQLErrors?.map(e => e.message).join(', ') || err.message,
            title: 'Error',
            type: 'error'
         })
      }
   }

   const handleRemoveLink = async (id) => {
      try {
         await updateOrganization({
            globalNavLinks: [{
               operation: 'remove',
               remove: id
            }]
         })

         showNotification({
            message: 'Main Menu link removed successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         showNotification({
            message: err?.graphQLErrors?.map(e => e.message).join(', ') || err.message,
            title: 'Error',
            type: 'error'
         })
      }
   }

   useEffect(() => {
      setLinkItems(organization?.globalNavLinks.filter(link => !link.app) || [])
   }, [organization?.globalNavLinks])

   if (loading) {
      return null
   }

   return (
      <>
         <ProtoReturnBar
            display="block[a-d] none[e-f]"
            returnLinkName={returnLinkName}
            returnLinkTo={returnLinkTo}
         />
         <View display="flex" justifyContent="center">
            <View
               maxWidth="100%[a-c] 650px[d-f]"
               padding="40px 20px">
               <View
                  width="100%"
                  display="flex"
                  flexFlow="column[a-c] row[d-f]">
                  <View flex="1 1 auto" paddingRight="30px[b-f]">
                     <ProtoHeadingBlock
                        title="Main Menu"
                        description="Customize the Main Menu that appears on your site. Add new links to pages, your content or external links. You can also organize the order that the pages appear in the navigation."
                     />
                  </View>
                  <View flex="0 0 auto" paddingTop="30px">
                     <Button
                        linkTo={`${match.url}/create`}
                        width="120px"
                        name="Add Link"
                        color={theme.protoSettingsHighlightBackgroundColor}
                        textColor={theme.protoSettingsHighlightForegroundColor}
                        textWeight="medium"
                        size="md"
                        textSize="0.9"
                        padding="24px"
                     />
                  </View>
               </View>
               <View width="100%" padding="12px 0px">
                  <Spacer size="5" />
                  <Text size="2" weight="medium" color="#000">
                     Main Menu Items
                  </Text>
               </View>
               {mutating && (
                  <View marginBottom="12px">
                     <NotificationInline
                        borderRadius="6px"
                        type="loading"
                        title="Updating..."
                        message="Update in progress"
                        paddingHorizontal="12px"
                        paddingVertical="12px"
                        textSize="1.5"
                     />
                  </View>
               )}
               <View
                  width="100%"
                  borderRadius="3px"
                  backgroundColor="#fff">
                  <View width="100%" backgroundColor="#f5f5f5">
                     <Popover.Provider>
                        <DragDropContext onDragEnd={handleDragEnd}>
                           <Droppable droppableId="droppable">
                              {(provided, snapshot) => (
                                 <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}>
                                    {linkItems.map((item, index) => {
                                       let entityType = 'External URL'
                                       let entityName

                                       if (item.page) {
                                          entityType = 'Custom Page'
                                          entityName = item.page.title
                                       } else if (item.initiative) {
                                          entityType = item.initiative.type.nameSingular
                                          entityName = item.initiative.name
                                       }

                                       return (
                                          <Draggable
                                             key={item.id}
                                             draggableId={item.id}
                                             index={index}>
                                             {(elementProvided, elementSnapshot) => (
                                                <div
                                                   ref={elementProvided.innerRef}
                                                   {...elementProvided.draggableProps}
                                                   style={{
                                                      ...elementProvided.draggableProps.style,
                                                      left: '0px !important'
                                                   }}>
                                                   <NavLinkListItemRow
                                                      archived={item?.page?.archived ||
                                                            item?.initiative?.archived}
                                                      draggable
                                                      dragHandleProps={
                                                         elementProvided.dragHandleProps
                                                      }
                                                      entityName={entityName}
                                                      entityType={entityType}
                                                      isDragging={elementSnapshot.isDragging}
                                                      name={item.name}
                                                      unpublished={item.__typename === 'NavInternalLinkElement' &&
                                                         !item?.page?.published &&
                                                         !item?.initiative?.privacy === 'draft'}
                                                      url={item.url}
                                                      action={(
                                                         <Popover.Anchor
                                                            meta={item}
                                                            tabIndex={index}>
                                                            <MoreIcon
                                                               width="24px"
                                                               height="24px"
                                                               color="#aaa"
                                                            />
                                                         </Popover.Anchor>
                                                      )}
                                                   />
                                                </div>
                                             )}
                                          </Draggable>
                                       )
                                    })}
                                    {provided.placeholder}
                                 </div>
                              )}
                           </Droppable>
                        </DragDropContext>
                        <Popover.View
                           anchorOriginHorizontal="right"
                           anchorOriginVertical="top"
                           backgroundColor="#fff"
                           borderRadius="3px"
                           width="150px">
                           {globalNavLink => (
                              <View
                                 float="left"
                                 width="100%"
                                 backgroundColor="#fff"
                                 borderRadius="3px"
                                 boxShadow="3px 3px 12px rgba(0,0,0,0.2)">
                                 {/* TODO: For now, while pages are pretending to be
                                 custom apps, the only way to remove a link or rename it is
                                 to update the app itself. This is to prevent unnecessary confusion
                                 PR #692 */}
                                 {globalNavLink.__typename === 'NavInternalLinkElement' && (
                                    <ListMenu buttonSize="sm">
                                       <ListMenuButton
                                          linkTo={globalNavLink.app
                                             ? adminAppUrl(globalNavLink.app)
                                             : adminCustomPageUrl(globalNavLink.page)
                                          }
                                          name="Edit"
                                       />
                                    </ListMenu>
                                 )}
                                 {globalNavLink.__typename === 'NavExternalLinkElement' && (
                                    <ListMenu buttonSize="sm">
                                       <ListMenuButton
                                          linkTo={`${match.url}/${globalNavLink.id}/edit`}
                                          name="Edit Link Name"
                                       />
                                       <ListMenuButton
                                          onClick={() => handleRemoveLink(globalNavLink.id)}
                                          name="Remove link"
                                       />
                                    </ListMenu>
                                 )}
                              </View>
                           )}
                        </Popover.View>
                     </Popover.Provider>
                  </View>
               </View>
               <Spacer size="6" />
               <ActionListItemRow
                  name="Logos"
                  description="Customize the logos used in your main menu, login page, email notifications and favicon."
                  action={(
                     <Link to={`${match.url}/logo-editor`}>
                        <Button
                           width="78px"
                           size="xs"
                           name="Edit"
                           color="#eee"
                           textColor="#222"
                        />
                     </Link>
                  )}
                  icon={(
                     <LogoOutline24Icon
                        strokeWidth="2px"
                        height="20px"
                        width="20px"
                        color="#aaa"
                     />
                  )}
               />
            </View>
         </View>
         <ModalRoute path={`${match.path}/create`}>
            {({ open, onCloseComplete, onCloseTrigger }) => (
               <Modal
                  borderRadius="6px"
                  width="330px"
                  onCloseTrigger={onCloseTrigger}
                  onCloseComplete={onCloseComplete}
                  open={open}>
                  <LinkCreate
                     onCloseButtonClick={onCloseTrigger}
                     onCreate={onCloseTrigger}
                  />
               </Modal>
            )}
         </ModalRoute>
         <ModalRoute path={`${match.path}/:navLinkId/edit`}>
            {({ open, onCloseComplete, onCloseTrigger }) => (
               <Modal
                  borderRadius="6px"
                  width="270px"
                  onCloseTrigger={onCloseTrigger}
                  onCloseComplete={onCloseComplete}
                  open={open}>
                  <LinkEdit
                     onCloseButtonClick={onCloseTrigger}
                     onUpdate={onCloseTrigger}
                  />
               </Modal>
            )}
         </ModalRoute>
         <ModalRoute path={`${match.url}/logo-editor`}>
            {({ open, onCloseComplete, onCloseTrigger }) => (
               <Modal
                  borderRadius="6px"
                  width="100%[a] 420px[b] 90%[c] 960px[d-f]"
                  height="100%[a] 80%[b] 95%[c] 80%[d-f]"
                  maxWidth="720px[c]"
                  maxHeight="600px[d-f]"
                  onCloseComplete={onCloseComplete}
                  onCloseTrigger={onCloseTrigger}
                  open={open}>
                  <LogoEditor onCloseButtonClick={onCloseTrigger} />
               </Modal>
            )}
         </ModalRoute>
      </>
   )
}

AdminWorkspaceMainMenu.propTypes = {
   returnLinkName: PropTypes.string.isRequired,
   returnLinkTo: PropTypes.string.isRequired
}

export default AdminWorkspaceMainMenu
